import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Button,
  Select,
  useToast,
  Spinner,
  Center,
  Badge,
  Progress,
  Flex,
  Icon,
  VStack,
  HStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  FiUsers,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiPlus,
  FiChevronUp,
  FiChevronDown,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../atsService";
import { jobRequisitionService } from "../jobRequisitionService";
import { FiSearch, FiFilter, FiDownload } from "react-icons/fi";
import { atsUtils } from "../atsUtils";
import CreateApplicationModal from "./CreateApplicationModal";

const ATSDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requisitions, setRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [selectedRequisition, setSelectedRequisition] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedRequisitionForCreate, setSelectedRequisitionForCreate] =
    useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    department: "all",
    location: "all",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "postedDate",
    direction: "desc",
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequisitions();
    fetchDashboardStats();
  }, [selectedRequisition]);

  const fetchRequisitions = async () => {
    try {
      // Get requisitions from actual API
      const { requisitions } = await jobRequisitionService.getAllRequisitions();
      const data = requisitions || [];
      setRequisitions(data);
      applyFilters(searchTerm, filters, data);
    } catch (error) {
      console.error("Error fetching requisitions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch job requisitions",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const params = selectedRequisition
        ? { requisitionId: selectedRequisition }
        : {};
      const response = await dashboardService.getDashboardStats(params);
      setStats(response.data.data);
    } catch (error) {
      console.log("Using sample data due to API error:", error.message);
      // Use sample data for demonstration
      setStats({
        totalApplications: 45,
        conversionRate: 12.5,
        pendingInterviews: 8,
        hiredCount: 5,
        stageBreakdown: [
          { _id: "applied", count: 15, avgScore: 75 },
          { _id: "screening", count: 12, avgScore: 78 },
          { _id: "phone_screen", count: 8, avgScore: 82 },
          { _id: "assessment", count: 6, avgScore: 85 },
          { _id: "interview_1", count: 4, avgScore: 88 },
        ],
      });

      toast({
        title: "Using Sample Data",
        description:
          "Connected to backend but showing sample data for demonstration",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Button handlers
  const handleCreateApplication = (requisitionId = null) => {
    setSelectedRequisitionForCreate(requisitionId);
    setIsCreateModalOpen(true);
  };

  const handleReviewApplications = () => {
    if (selectedRequisition) {
      navigate(`/recruiting/applications/${selectedRequisition}`);
    } else {
      toast({
        title: "Select Requisition",
        description: "Please select a requisition to review applications",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleScheduleInterviews = () => {
    toast({
      title: "Schedule Interviews",
      description: "Interview scheduling feature coming soon!",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleGenerateReports = () => {
    toast({
      title: "Generate Reports",
      description: "Report generation feature coming soon!",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    applyFilters(term, filters);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    applyFilters(searchTerm, newFilters);
  };

  const applyFilters = (search, filterValues, data = requisitions) => {
    let filtered = [...data];

    // Apply search filter
    if (search) {
      filtered = filtered.filter(
        (req) =>
          req.jobTitle?.toLowerCase().includes(search.toLowerCase()) ||
          req.department?.toLowerCase().includes(search.toLowerCase()) ||
          req.location?.toLowerCase().includes(search.toLowerCase()) ||
          req._id?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Apply status filter
    if (filterValues.status !== "all") {
      filtered = filtered.filter((req) => req.status === filterValues.status);
    }

    // Apply department filter
    if (filterValues.department !== "all") {
      filtered = filtered.filter(
        (req) => req.department === filterValues.department
      );
    }

    // Apply location filter
    if (filterValues.location !== "all") {
      filtered = filtered.filter(
        (req) => req.location === filterValues.location
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredRequisitions(filtered);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    applyFilters(searchTerm, filters);
  };

  const getUniqueValues = (field) => {
    const values = requisitions.map((req) => req[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      department: "all",
      location: "all",
    });
    setSearchTerm("");
    applyFilters("", {
      status: "all",
      department: "all",
      location: "all",
    });
  };

  const StatCard = ({ title, value, icon, color, helpText, trend }) => (
    <Card>
      <CardBody>
        <Stat>
          <StatLabel fontSize="sm" color="gray.600">
            <HStack>
              <Icon as={icon} />
              <Text>{title}</Text>
            </HStack>
          </StatLabel>
          <StatNumber fontSize="2xl" color={color}>
            {value}
          </StatNumber>
          {helpText && (
            <StatHelpText>
              {trend && <StatArrow type={trend.type} />}
              {helpText}
            </StatHelpText>
          )}
        </Stat>
      </CardBody>
    </Card>
  );

  const StageProgressCard = ({ stageData }) => {
    const totalApplications = stats?.totalApplications || 0;
    const stageCount = stageData?.count || 0;
    const percentage =
      totalApplications > 0 ? (stageCount / totalApplications) * 100 : 0;

    return (
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm">
                {atsUtils.formatApplicationStatus(stageData._id)}
              </Text>
              <Badge colorScheme={atsUtils.getStatusColor(stageData._id)}>
                {stageCount}
              </Badge>
            </HStack>
            <Progress
              value={percentage}
              colorScheme={atsUtils.getStatusColor(stageData._id)}
              size="sm"
            />
            <Text fontSize="xs" color="gray.500">
              {percentage.toFixed(1)}% of total applications
            </Text>
            {stageData.avgScore && (
              <Text fontSize="xs" color="gray.500">
                Avg Score: {stageData.avgScore.toFixed(1)}/100
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  const FilterModal = () => (
    <Modal
      isOpen={isFilterModalOpen}
      onClose={() => setIsFilterModalOpen(false)}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Filter Requisitions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="on_hold">On Hold</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Department</FormLabel>
              <Select
                value={filters.department}
                onChange={(e) =>
                  handleFilterChange("department", e.target.value)
                }
              >
                <option value="all">All Departments</option>
                {getUniqueValues("department").map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Location</FormLabel>
              <Select
                value={filters.location}
                onChange={(e) => handleFilterChange("location", e.target.value)}
              >
                <option value="all">All Locations</option>
                {getUniqueValues("location").map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={clearFilters}>
            Clear All
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => setIsFilterModalOpen(false)}
          >
            Apply Filters
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg" mb={2}>
              Applicant tracking system Dashboard
            </Heading>
            <Text color="gray.600">
              Comprehensive view of your recruitment pipeline
            </Text>
          </Box>
          <HStack spacing={4}>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={handleCreateApplication}
            >
              New Application
            </Button>
            <Button variant="outline" onClick={fetchDashboardStats}>
              Refresh
            </Button>
          </HStack>
        </Flex>

        {/* Job Requisitions Table */}
        <Card shadow="sm">
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">Job Requisitions</Heading>
              <HStack spacing={4}>
                <InputGroup maxW="300px">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search requisitions..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </InputGroup>
                <Button
                  leftIcon={<FiFilter />}
                  variant="ghost"
                  onClick={() => setIsFilterModalOpen(true)}
                >
                  Filter
                </Button>
                <Button leftIcon={<FiDownload />} variant="ghost">
                  Export
                </Button>
              </HStack>
            </Flex>
            {/* Active Filters Display */}
            {(searchTerm ||
              filters.status !== "all" ||
              filters.department !== "all" ||
              filters.location !== "all") && (
              <Flex mt={4} wrap="wrap" gap={2}>
                {searchTerm && (
                  <Badge colorScheme="blue" variant="subtle">
                    Search: "{searchTerm}"
                  </Badge>
                )}
                {filters.status !== "all" && (
                  <Badge colorScheme="green" variant="subtle">
                    Status: {atsUtils.formatApplicationStatus(filters.status)}
                  </Badge>
                )}
                {filters.department !== "all" && (
                  <Badge colorScheme="purple" variant="subtle">
                    Department: {filters.department}
                  </Badge>
                )}
                {filters.location !== "all" && (
                  <Badge colorScheme="orange" variant="subtle">
                    Location: {filters.location}
                  </Badge>
                )}
                <Button size="xs" variant="ghost" onClick={clearFilters}>
                  Clear All
                </Button>
              </Flex>
            )}
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th cursor="pointer" onClick={() => handleSort("_id")}>
                    <HStack spacing={1}>
                      <Text>ID</Text>
                      {sortConfig.key === "_id" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort("jobTitle")}>
                    <HStack spacing={1}>
                      <Text>Position</Text>
                      {sortConfig.key === "jobTitle" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort("department")}>
                    <HStack spacing={1}>
                      <Text>Department</Text>
                      {sortConfig.key === "department" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort("status")}>
                    <HStack spacing={1}>
                      <Text>Status</Text>
                      {sortConfig.key === "status" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th>Applications</Th>
                  <Th cursor="pointer" onClick={() => handleSort("postedDate")}>
                    <HStack spacing={1}>
                      <Text>Posted Date</Text>
                      {sortConfig.key === "postedDate" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRequisitions.map((req) => (
                  <Tr
                    key={req._id}
                    onClick={() => navigate(`/recruiting/ats/${req._id}`)}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                  >
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {req._id?.slice(-8) || "N/A"}
                      </Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{req.jobTitle || "N/A"}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {req.location || "N/A"}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge variant="subtle" colorScheme="blue">
                        {req.department || "N/A"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={atsUtils.getStatusColor(req.status)}>
                        {atsUtils.formatApplicationStatus(req.status) || "N/A"}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">
                          {req.applicationsCount || 0}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          applications
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">
                          {req.postedDate
                            ? new Date(req.postedDate).toLocaleDateString()
                            : "N/A"}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {req.postedDate
                            ? new Date(req.postedDate).toLocaleTimeString()
                            : ""}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Review applications"
                          icon={<FiEye />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/recruiting/ats/${req._id}`);
                          }}
                        />
                        <IconButton
                          aria-label="Add application"
                          icon={<FiPlus />}
                          size="sm"
                          colorScheme="green"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateApplication(req._id);
                          }}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {filteredRequisitions.length === 0 && (
              <Center p={8}>
                <Text color="gray.500">
                  {searchTerm
                    ? "No matching requisitions found"
                    : "No requisitions found"}
                </Text>
              </Center>
            )}
          </CardBody>
        </Card>

        <Divider />

        {/* Key Metrics */}
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          <StatCard
            title="Total Applications"
            value={stats?.totalApplications || 0}
            icon={FiUsers}
            color="blue.500"
            helpText="Active applications"
          />
          <StatCard
            title="Conversion Rate"
            value={`${(stats?.conversionRate || 0).toFixed(1)}%`}
            icon={FiTrendingUp}
            color="green.500"
            helpText="Applications to hires"
            trend={{
              type: stats?.conversionRate > 10 ? "increase" : "decrease",
            }}
          />
          <StatCard
            title="Pending Interviews"
            value={stats?.pendingInterviews || 0}
            icon={FiClock}
            color="orange.500"
            helpText="Scheduled interviews"
          />
          <StatCard
            title="Hired"
            value={stats?.hiredCount || 0}
            icon={FiCheckCircle}
            color="green.500"
            helpText="Successful hires"
          />
        </Grid>

        {/* Pipeline Overview */}
        <Card>
          <CardHeader>
            <Heading size="md">Recruitment Pipeline</Heading>
            <Text color="gray.600">
              Current distribution of applications across stages
            </Text>
          </CardHeader>
          <CardBody>
            <Grid
              templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
              gap={4}
            >
              {stats?.stageBreakdown?.map((stage) => (
                <StageProgressCard key={stage._id} stageData={stage} />
              ))}
            </Grid>
          </CardBody>
        </Card>

        {/* Quick Actions */}
        <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={4}>
          <Card>
            <CardHeader>
              <Heading size="md">Quick Actions</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleCreateApplication}
                >
                  Create New Application
                </Button>
                <Button
                  leftIcon={<FiEye />}
                  colorScheme="green"
                  variant="outline"
                  onClick={handleReviewApplications}
                >
                  Review Applications
                </Button>
                <Button
                  leftIcon={<FiClock />}
                  colorScheme="orange"
                  variant="outline"
                  onClick={handleScheduleInterviews}
                >
                  Schedule Interviews
                </Button>
                <Button
                  leftIcon={<FiCheckCircle />}
                  colorScheme="purple"
                  variant="outline"
                  onClick={handleGenerateReports}
                >
                  Generate Reports
                </Button>
              </VStack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Heading size="md">Recent Activity</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" color="gray.500">
                  No recent activity to display
                </Text>
                {/* TODO: Add recent activity feed */}
              </VStack>
            </CardBody>
          </Card>
        </Grid>
      </VStack>

      {/* Create Application Modal */}
      <CreateApplicationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        requisitionId={selectedRequisitionForCreate}
        onSuccess={() => {
          fetchRequisitions();
          fetchDashboardStats();
        }}
      />

      {/* Filter Modal */}
      <FilterModal />
    </Box>
  );
};

export default ATSDashboard;
